/**
 * useCan(action, target) — returns true if the current user can perform
 * `action` at the given target scope, given their memberships.
 *
 * Reads from AuthContext for the user (and triggers a lazy fetch of the
 * memberships on first call). Cached per-user inside a small in-module
 * store so multiple `useCan` calls in the same render don't re-fetch.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authApi, type ScopeRole, type ScopeType } from '@/services/api';
import { canActOn, type Action, type HeldMembership } from '@/lib/permissions';

// In-module cache so we don't refetch on every consumer mount.
let cachedUserId: string | null = null;
let cachedMemberships: HeldMembership[] = [];
let inflight: Promise<HeldMembership[]> | null = null;

async function fetchMemberships(): Promise<HeldMembership[]> {
  if (inflight) return inflight;
  inflight = authApi
    .getMyMemberships()
    .then((res) => {
      const list: HeldMembership[] = res.memberships.map((m) => ({
        scopeType: m.scopeType as ScopeType,
        scopeId: m.scopeId,
        role: m.role as ScopeRole,
      }));
      cachedMemberships = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useMyMemberships(): { memberships: HeldMembership[]; loading: boolean } {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<HeldMembership[]>(
    user && cachedUserId === user.id ? cachedMemberships : [],
  );
  const [loading, setLoading] = useState<boolean>(!!user && cachedUserId !== user.id);

  useEffect(() => {
    if (!user) {
      cachedUserId = null;
      cachedMemberships = [];
      setMemberships([]);
      setLoading(false);
      return;
    }
    if (cachedUserId === user.id) {
      setMemberships(cachedMemberships);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMemberships()
      .then((list) => {
        cachedUserId = user.id;
        setMemberships(list);
      })
      .catch(() => setMemberships([]))
      .finally(() => setLoading(false));
  }, [user?.id]);

  return { memberships, loading };
}

export function useCan(
  action: Action,
  target: { scopeType: ScopeType; scopeId: string } | null | undefined,
  ancestors: { scopeType: ScopeType; scopeId: string }[] = [],
): boolean {
  const { memberships } = useMyMemberships();
  if (!target) return false;
  return canActOn(memberships, action, target, ancestors);
}
